import { supabase, db } from '@/lib/supabase'
import { appFetch, isAppApiConfigured } from '@/lib/appApi'
import { buildOrIlikeClause } from '@/lib/dashboard-v2/postgrestOrIlike'
import { DASHBOARD_EXPORT_MAX_ROWS, type DashboardExportResult } from '@/lib/dashboard-v2/dashboardExportLimits'

// Types
export interface OrderTax {
    id: string
    order_id: string
    store_id: string | null
    tax_name: string | null
    tax_rate: number | null
    tax_amount: number | null
    tax_type: 'percentage' | 'fixed' | null
    created_at: string
}

export interface OrderItem {
    id: string
    order_id: string
    store_id: string | null
    product_id: string | null
    variant_id: string | null
    variant_title?: string | null
    product_name: string
    product_sku: string | null
    product_image: string | null
    price: number
    purchase_price: number | null
    quantity: number
    subtotal: number
    created_at: string
}

export interface Order {
    id: string
    user_id: string
    buyer_id?: string | null
    order_number: string
    customer_name: string
    customer_email: string | null
    customer_phone: string
    shipping_address: string
    shipping_city: string
    shipping_governorate: string | null
    subtotal: number
    tax_total: number
    shipping_cost: number
    discount_amount: number
    discount?: number
    total: number
    status: 'new' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'returned' | 'abandoned' | 'pending_whatsapp_confirmation'
    payment_method: string
    payment_status: 'pending' | 'pending_payment' | 'paid' | 'failed' | 'refunded'
    notes: string | null
    admin_notes: string | null
    created_at: string
    updated_at: string
    delivered_at: string | null
    sales_page_id: string | null
    store_id: string | null
    coupon_id: string | null
    idempotency_key: string | null
    metadata?: Record<string, any> | null
    order_items?: OrderItem[]
    order_taxes?: OrderTax[]
}

export type OrderStatus = Order['status']
export type OrderInsert = Omit<Order, 'id' | 'user_id' | 'order_number' | 'created_at' | 'updated_at' | 'delivered_at'>
export type OrderUpdate = Partial<Pick<Order, 'status' | 'payment_status' | 'notes' | 'admin_notes'>>

export interface OrderRisk {
    total_orders: number
    returned_orders: number
    risk_level: 'low' | 'medium' | 'high'
    risk_reason: string | null
}

export type OrderListPageStatus = OrderStatus | 'all'

export type OrderListPageResult = { rows: Order[]; totalCount: number }

const MAX_ORDER_MATCHING_IDS = 10_000

// Orders Service
export const ordersService = {
    // Get all orders for a specific store (paginated)
    async getAll(storeId?: string, page = 0, pageSize = 50): Promise<Order[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        let query = db
            .from('orders')
            .select(`
        *,
        order_items (*)
      `)

        // 🔒 Store-scoped filtering (RLS also enforces this)
        if (storeId) {
            query = query.eq('store_id', storeId)
        }

        const { data, error } = await query
            .order('created_at', { ascending: false })
            .range(page * pageSize, (page + 1) * pageSize - 1)

        if (error) throw error
        return data || []
    },

    /**
     * Dashboard: server pagination + exact count (filters, sort, range).
     * Includes `order_items` for row actions (WhatsApp, etc.).
     */
    async listPage(
        storeId: string,
        opts: {
            page: number
            pageSize: number
            q: string
            sortKey: string | null
            sortDir: 'asc' | 'desc'
            status: OrderListPageStatus
        }
    ): Promise<OrderListPageResult> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        const page = Math.max(1, Math.floor(opts.page))
        const pageSize = Math.min(20, Math.max(1, Math.floor(opts.pageSize)))

        if (isAppApiConfigured()) {
            const params = new URLSearchParams()
            params.set('storeId', storeId)
            params.set('page', String(page))
            params.set('pageSize', String(pageSize))
            params.set('q', opts.q ?? '')
            params.set('sortKey', opts.sortKey ?? '')
            params.set('sortDir', opts.sortDir)
            params.set('status', opts.status)
            const j = await appFetch<{ ok: true; rows: Order[]; totalCount: number }>(
                `/api/app/orders?${params.toString()}`
            )
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return { rows: (j as { rows: Order[] }).rows, totalCount: (j as { totalCount: number }).totalCount ?? 0 }
        }

        const from = (page - 1) * pageSize
        const to = from + pageSize - 1

        let query = db
            .from('orders')
            .select(
                `
        *,
        order_items (*)
      `,
                { count: 'exact' }
            )
            .eq('store_id', storeId)

        if (opts.status !== 'all') {
            query = query.eq('status', opts.status)
        }

        const orClause = buildOrIlikeClause(['order_number', 'customer_name', 'customer_phone'], opts.q || '')
        if (orClause) query = query.or(orClause)

        const sortKey = opts.sortKey || ''
        const asc = opts.sortDir === 'asc'
        if (sortKey === 'number') query = query.order('created_at', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'customer') query = query.order('customer_name', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'status') query = query.order('status', { ascending: asc, nullsFirst: false })
        else if (sortKey === 'total') query = query.order('total', { ascending: asc, nullsFirst: false })
        else query = query.order('created_at', { ascending: false, nullsFirst: false })

        const { data, error, count } = await query.range(from, to)
        if (error) throw error
        return { rows: (data || []) as Order[], totalCount: count ?? 0 }
    },

    async listMatchingIds(
        storeId: string,
        opts: { q: string; status: OrderListPageStatus }
    ): Promise<string[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams()
            params.set('storeId', storeId)
            params.set('q', opts.q ?? '')
            params.set('status', opts.status)
            const j = await appFetch<{ ok: true; ids: string[] }>(`/api/app/orders/matching-ids?${params.toString()}`)
            if (!j || typeof j !== 'object' || !Array.isArray((j as { ids?: unknown }).ids)) {
                throw new Error('Invalid API response')
            }
            return (j as { ids: string[] }).ids
        }

        let query = db.from('orders').select('id').eq('store_id', storeId).limit(MAX_ORDER_MATCHING_IDS)

        if (opts.status !== 'all') {
            query = query.eq('status', opts.status)
        }

        const orClause = buildOrIlikeClause(['order_number', 'customer_name', 'customer_phone'], opts.q || '')
        if (orClause) query = query.or(orClause)

        const { data, error } = await query.order('created_at', { ascending: false, nullsFirst: false })
        if (error) throw error
        return (data || []).map((r: { id: string }) => r.id)
    },

    async fetchAllMatchingForExport(
        storeId: string,
        opts: { q: string; status: OrderListPageStatus }
    ): Promise<DashboardExportResult<Order>> {
        const out: Order[] = []
        const batch = 500
        let page = 1
        while (true) {
            const { rows, totalCount } = await ordersService.listPage(storeId, {
                page,
                pageSize: batch,
                q: opts.q,
                sortKey: null,
                sortDir: 'desc',
                status: opts.status,
            })
            out.push(...rows)
            if (out.length >= DASHBOARD_EXPORT_MAX_ROWS) {
                return { rows: out.slice(0, DASHBOARD_EXPORT_MAX_ROWS), truncated: true }
            }
            if (out.length >= totalCount || rows.length === 0) break
            page += 1
            if (page > 200) break
        }
        return { rows: out, truncated: false }
    },

    // Get orders by status (store-scoped)
    async getByStatus(status: OrderStatus, storeId?: string): Promise<Order[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        let query = db
            .from('orders')
            .select(`
        *,
        order_items (*)
      `)
            .eq('status', status)

        if (storeId) {
            query = query.eq('store_id', storeId)
        }

        const { data, error } = await query
            .order('created_at', { ascending: false })

        if (error) throw error
        return data || []
    },

    // Get single order by ID (RLS enforces store ownership)
    async getById(id: string): Promise<Order | null> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; order: Order | null }>(
                `/api/app/orders/${encodeURIComponent(id)}`
            )
            if (!j || typeof j !== 'object' || !('order' in j)) {
                throw new Error('Invalid API response')
            }
            return (j as { order: Order | null }).order
        }

        const { data, error } = await db
            .from('orders')
            .select(`
        *,
        order_items (*, product_variants (variant_title)),
        order_taxes (id, tax_name, tax_rate, tax_amount, tax_type, created_at)
      `)
            .eq('id', id)
            // 🔒 RLS handles ownership via store_id → stores.user_id
            .single()

        if (error) {
            if (error.code === 'PGRST116') return null
            throw error
        }
        return data
    },

    // Create new order (admin/dashboard use — direct insert, no atomicity guarantees)
    async create(order: OrderInsert, items: Omit<OrderItem, 'id' | 'order_id' | 'created_at'>[], sellerId?: string): Promise<Order> {
        let finalSellerId = sellerId;

        if (!finalSellerId) {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error('Not authenticated')
            finalSellerId = user.id
        }

        // Insert order
        const { data: newOrder, error: orderError } = await db
            .from('orders')
            .insert({
                ...order,
                user_id: finalSellerId
            })
            .select()
            .single()

        if (orderError) throw orderError

        // Insert order items
        if (items.length > 0) {
            const { error: itemsError } = await db
                .from('order_items')
                .insert(items.map(item => ({
                    ...item,
                    order_id: newOrder.id
                })))

            if (itemsError) throw itemsError
        }

        return this.getById(newOrder.id) as Promise<Order>
    },

    // Update order status (RLS enforces store ownership)
    // Returns full order with order_items so detail page cache stays correct after status change
    async updateStatus(id: string, status: OrderStatus): Promise<Order> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; order: Order }>(
                `/api/app/orders/${encodeURIComponent(id)}/status`,
                { method: 'PATCH', json: { status } }
            )
            if (!j || typeof j !== 'object' || !(j as { order?: unknown }).order) {
                throw new Error('Invalid API response')
            }
            return (j as { order: Order }).order
        }

        const { data, error } = await db
            .from('orders')
            .update({ status })
            .eq('id', id)
            .select(`
                *,
                order_items (*)
            `)
            .single()

        if (error) throw error
        return data
    },

    // Update order (RLS enforces store ownership)
    async update(id: string, updates: OrderUpdate): Promise<Order> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; order: Order }>(
                `/api/app/orders/${encodeURIComponent(id)}`,
                { method: 'PATCH', json: { updates } }
            )
            if (!j || typeof j !== 'object' || !(j as { order?: unknown }).order) {
                throw new Error('Invalid API response')
            }
            return (j as { order: Order }).order
        }

        const { data, error } = await db
            .from('orders')
            .update(updates)
            .eq('id', id)
            // 🔒 RLS handles ownership via store_id → stores.user_id
            .select()
            .single()

        if (error) throw error
        return data
    },

    // Get order statistics (server-side, store-scoped)
    async getStats(storeId: string): Promise<{
        total: number
        new: number
        confirmed: number
        shipped: number
        delivered: number
        cancelled: number
        returned: number
        abandoned: number
    }> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const j = await appFetch<{
                ok: true
                stats: {
                    total: number
                    new: number
                    confirmed: number
                    shipped: number
                    delivered: number
                    cancelled: number
                    returned: number
                    abandoned: number
                }
            }>(`/api/app/orders/stats?${new URLSearchParams({ storeId }).toString()}`)
            if (!j || typeof j !== 'object' || !(j as { stats?: unknown }).stats) {
                throw new Error('Invalid API response')
            }
            return (j as {
                stats: {
                    total: number
                    new: number
                    confirmed: number
                    shipped: number
                    delivered: number
                    cancelled: number
                    returned: number
                    abandoned: number
                }
            }).stats
        }

        const { data: rows, error } = await db
            .from('orders')
            .select('status')
            .eq('store_id', storeId)

        if (error) throw error

        const stats = {
            total: 0,
            new: 0,
            confirmed: 0,
            shipped: 0,
            delivered: 0,
            cancelled: 0,
            returned: 0,
            abandoned: 0,
        }
        for (const r of rows || []) {
            stats.total++
            const s = String(r.status || '')
            if (s === 'new') stats.new++
            else if (s === 'confirmed') stats.confirmed++
            else if (s === 'shipped') stats.shipped++
            else if (s === 'delivered') stats.delivered++
            else if (s === 'cancelled') stats.cancelled++
            else if (s === 'returned') stats.returned++
            else if (s === 'abandoned') stats.abandoned++
        }
        return stats
    },

    // Search orders (store-scoped via RLS)
    async search(query: string, storeId?: string): Promise<Order[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams()
            if (storeId) params.set('storeId', storeId)
            params.set('q', query)
            const j = await appFetch<{ ok: true; rows: Order[] }>(`/api/app/orders/search?${params.toString()}`)
            if (!j || typeof j !== 'object' || !Array.isArray((j as { rows?: unknown }).rows)) {
                throw new Error('Invalid API response')
            }
            return (j as { rows: Order[] }).rows
        }

        let q = db
            .from('orders')
            .select(`
        *,
        order_items (*)
      `)

        if (storeId) {
            q = q.eq('store_id', storeId)
        }

        const orClause = buildOrIlikeClause(['order_number', 'customer_name', 'customer_phone'], query)
        const q2 = orClause ? q.or(orClause) : q
        const { data, error } = await q2.order('created_at', { ascending: false })

        if (error) throw error
        return data || []
    },

    // Check risk for a specific order (P2.2)
    async checkOrderRisk(phone: string, storeId: string): Promise<OrderRisk> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Not authenticated')
        if (!storeId || !phone.trim()) {
            return { total_orders: 0, returned_orders: 0, risk_level: 'low', risk_reason: null }
        }

        if (isAppApiConfigured()) {
            const j = await appFetch<{ ok: true; risk: OrderRisk }>(
                `/api/app/orders/risk?${new URLSearchParams({ storeId, phone: phone.trim() }).toString()}`
            )
            if (!j || typeof j !== 'object' || !(j as { risk?: unknown }).risk) {
                throw new Error('Invalid API response')
            }
            return (j as { risk: OrderRisk }).risk
        }

        const { data: orders, error } = await db
            .from('orders')
            .select('status')
            .eq('store_id', storeId)
            .eq('customer_phone', phone.trim())

        if (error) throw error

        const list = orders || []
        const total_orders = list.length
        const returned_orders = list.filter(o => o.status === 'returned').length
        let risk_level: OrderRisk['risk_level'] = 'low'
        let risk_reason: string | null = null
        if (returned_orders >= 2) {
            risk_level = 'high'
            risk_reason = 'Plusieurs retours sur ce numéro'
        } else if (returned_orders === 1 && total_orders >= 3) {
            risk_level = 'medium'
            risk_reason = 'Retour signalé sur un historique actif'
        }

        return { total_orders, returned_orders, risk_level, risk_reason }
    }
}
